"use client"

import type React from "react"

import {
  Gamepad2,
  Calculator,
  BookOpen,
  Beaker,
  Music,
  Rocket,
  CheckCircle2,
  Calendar,
  Edit2,
  Trash2,
} from "lucide-react"

interface Goal {
  id: string
  title: string
  description: string
  progress: number
  dueDate?: string
  completedDate?: string
  icon?: string
  iconEmoji?: string
  iconBgColor: string
  iconColor: string
  isCompleted: boolean
  tier?: string
  goalType?: string
}

interface GoalCardProps {
  goal: Goal
  onDelete: (id: string) => void
  onEdit: (id: string, updatedGoal: Partial<Goal>) => void
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Gamepad2,
  Calculator,
  BookOpen,
  Beaker,
  Music,
  Rocket,
}

export default function GoalCard({ goal, onDelete, onEdit }: GoalCardProps) {
  const progressBarColor = goal.isCompleted ? "bg-emerald-500" : "bg-indigo-600"
  const progressTextColor = goal.isCompleted ? "text-emerald-500" : "text-indigo-600"

  const IconComponent = goal.icon ? (iconMap[goal.icon] ?? BookOpen) : null

  return (
    <div
      className={`flex flex-col gap-5 rounded-2xl bg-slate-100 p-6 shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        goal.isCompleted ? "opacity-80" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{goal.title}</h3>
          <p className="mt-1 text-sm text-slate-500">{goal.description}</p>
        </div>
        <div className={`flex-shrink-0 rounded-full p-3 ${goal.iconBgColor}`}>
          {goal.iconEmoji
            ? <span className="text-2xl leading-none">{goal.iconEmoji}</span>
            : IconComponent
              ? <IconComponent className={`w-6 h-6 ${goal.iconColor}`} />
              : <BookOpen className={`w-6 h-6 ${goal.iconColor}`} />
          }
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-600">Progress</span>
          {goal.isCompleted ? (
            <div className={`flex items-center gap-1 text-sm font-bold ${progressTextColor}`}>
              <CheckCircle2 className="w-4 h-4" />
              <span>{goal.progress}%</span>
            </div>
          ) : (
            <span className={`text-sm font-bold ${progressTextColor}`}>{goal.progress}%</span>
          )}
        </div>
        <div className="h-2.5 w-full rounded-full bg-slate-200">
          <div
            className={`h-2.5 rounded-full transition-all ${progressBarColor}`}
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4 flex items-center justify-between mt-auto">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          {goal.isCompleted ? <CheckCircle2 className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
          <span>{goal.isCompleted ? `Completed: ${goal.completedDate}` : `Due: ${goal.dueDate}`}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(goal.id, { progress: Math.min(goal.progress + 10, 100) })}
            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-indigo-600"
            title="Increase progress"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(goal.id)}
            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-red-500"
            title="Delete goal"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
